import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { ExternalLink, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { NewsIntelligenceRow } from '@/hooks/useNewsIntelligence';

interface NewsIntelligenceTableProps {
  data: NewsIntelligenceRow[];
  onViewArticle: (article: NewsIntelligenceRow) => void;
}

type SortField = 'source' | 'title' | 'published_at' | 'priority' | 'signal';
type SortDirection = 'asc' | 'desc';

const getPriorityColor = (level: number | null) => {
  switch (level) {
    case 1: return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-100';
    case 2: return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-100';
    default: return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  }
};

const getPriorityLabel = (level: number | null) => {
  switch (level) {
    case 1: return 'High';
    case 2: return 'Medium';
    default: return 'Low';
  }
};

const getSignalColor = (signal: string | null) => {
  switch (signal) {
    case 'funding': return 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-100';
    case 'partnership': return 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-100';
    case 'clinical_data': return 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-100';
    case 'market_trend': return 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-100';
    case 'leadership': return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900 dark:text-cyan-100';
    case 'competitor_news': return 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-100';
    default: return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
  }
};

const getSignalLabel = (signal: string | null) => {
  if (!signal) return 'General';
  return signal.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

export function NewsIntelligenceTable({ data, onViewArticle }: NewsIntelligenceTableProps) {
  const [sortField, setSortField] = useState<SortField>('published_at');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      let aVal: any;
      let bVal: any;

      switch (sortField) {
        case 'source':
          aVal = (a.source_feed || '').toLowerCase();
          bVal = (b.source_feed || '').toLowerCase();
          break;
        case 'title':
          aVal = (a.title || '').toLowerCase();
          bVal = (b.title || '').toLowerCase();
          break;
        case 'published_at':
          aVal = new Date(a.published_at || 0).getTime();
          bVal = new Date(b.published_at || 0).getTime();
          break;
        case 'priority':
          aVal = a.priority_level || 99;
          bVal = b.priority_level || 99;
          break;
        case 'signal':
          aVal = a.signal_type || a.match_type || '';
          bVal = b.signal_type || b.match_type || '';
          break;
        default:
          return 0;
      }

      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
      } else {
        return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
      }
    });
  }, [data, sortField, sortDirection]);

  const SortButton = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field;
    return (
      <Button
        variant="ghost"
        size="sm"
        className={cn("-ml-3 h-8", isActive && "bg-accent")}
        onClick={() => handleSort(field)}
      >
        {children}
        {isActive ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="ml-2 h-4 w-4" />
          ) : (
            <ArrowDown className="ml-2 h-4 w-4" />
          )
        ) : (
          <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />
        )}
      </Button>
    );
  };

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[110px]">
              <SortButton field="published_at">Date</SortButton>
            </TableHead>
            <TableHead className="w-[140px]">
              <SortButton field="source">Source</SortButton>
            </TableHead>
            <TableHead className="min-w-[300px]">
              <SortButton field="title">Title</SortButton>
            </TableHead>
            <TableHead className="w-[100px]">
              <SortButton field="priority">Priority</SortButton>
            </TableHead>
            <TableHead className="w-[120px]">
              <SortButton field="signal">Signal</SortButton>
            </TableHead>
            <TableHead className="w-[100px] text-center">Prospect</TableHead>
            <TableHead className="w-[100px] text-right">Full Article</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedData.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                No articles found matching the current filters.
              </TableCell>
            </TableRow>
          ) : (
            sortedData.map((article) => {
              const formattedDate = article.published_at 
                ? format(new Date(article.published_at), 'MMM d, yyyy')
                : '-';
              const signal = article.signal_type || article.match_type;

              return (
                <TableRow
                  key={article.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onViewArticle(article)}
                >
                  <TableCell>
                    <span className="text-sm text-muted-foreground">{formattedDate}</span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm font-medium truncate block max-w-[130px]">
                      {article.source_feed || 'Unknown'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <p className="text-sm line-clamp-2 font-medium">
                      {article.title}
                    </p>
                    {article.executive_briefing && (
                      <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">
                        {article.executive_briefing.substring(0, 100)}...
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge className={cn("text-xs", getPriorityColor(article.priority_level))}>
                      {getPriorityLabel(article.priority_level)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={cn("text-xs", getSignalColor(signal))}>
                      {getSignalLabel(signal)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center">
                    {article.prospect_company_name ? (
                      <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                        {article.prospect_company_name.length > 15 
                          ? article.prospect_company_name.substring(0, 15) + '...'
                          : article.prospect_company_name}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {article.url && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(article.url!, '_blank');
                        }}
                        title="Read original article"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
